/**
 * App Layout Component
 * Main application layout using shared AppHeader + AppSidebar from @bizfirst/common-react.
 *
 * Sidebar behaviour (per requirements):
 *  - Items WITH submenus (NavGroup)  → clicking toggles dropdown
 *  - Items WITHOUT submenus (NavElement) → clicking navigates; sidebar collapses/expands
 *    via the hamburger toggle in the header.
 *
 * Flow Studio has three flat items (no submenus), so AppSidebar is used in
 * `elements` mode. The collapse toggle lives in AppHeader.
 */

import { useState, useEffect, useRef } from 'react';
import {
  AppHeader,
  AppSidebar,
  BizFirstLinks,
  type NavElement,
  DashboardNavIcon,
  DesignerNavIcon,
  SettingsNavIcon,
  PackageIcon,
  EyeIcon,
  InboxIcon,
  BuildWithAIButton,
} from '@bizfirst/common-react';
import { AuthHeaderControls } from '@bizfirst/common-auth-react';
import { useAuthStore } from '@passport/store';
import { DEVELOPER_PORTAL_URL } from '@passport/app-urls';
import { useNotifications, isNotificationUnread } from '@bizfirst/notifications-react';
import { useGlobalAppData } from '@bizfirst/global-react';
import { useUIStore, MOBILE_MAX_WIDTH, DESKTOP_MIN_WIDTH } from '@flow-studio/store';
import { FlowFooter } from '@flow-studio/flow-footer';
import type { BadgeInteractionStrategy, BadgeVisibilityScope } from '@flow-studio/flow-observer-core';
import { FlowObserverPanelEngine } from '@flow-studio/flow-observer-core';
import { isAnyModalOpen } from '../Support/Modal';
import { shouldPersistNavCollapsed } from './layoutBreakpoints';
import './AppLayout.css';

// ─── Types ───────────────────────────────────────────────────────────────────

export type MenuView = 'dashboard' | 'designer' | 'settings' | 'observer' | 'hil-test';

export interface BreadcrumbItem {
  label: string;
  onClick?: () => void;
}

export interface AppLayoutProps {
  children?: React.ReactNode;
  activeView?: MenuView;
  onViewChange?: (view: MenuView) => void;
  headerProps?: { breadcrumbs?: BreadcrumbItem[] };
  /** 2026-09-13 pivot: "Build using AI" moved from a left-sidebar NavElement to a header
   *  button — Binoy wants it reachable the same way (a top-menu button, not a sidebar item)
   *  across all three studios (Flow Studio, App Studio, Form Studio), and App Studio has no
   *  sidebar at all to put a NavElement in. Omitted entirely if the host doesn't wire it up. */
  onBuildWithAI?: () => void;
}

// ─── Constants ───────────────────────────────────────────────────────────────

// 'Dev Tools' is a deliberate exception in this list: every other item is an internal SPA
// view (handled by handleNavigate below via ROUTE_TO_VIEW), but Dev Tools now sends the
// browser to the standalone developer-portal app instead — see the DEV_TOOLS_ROUTE
// special-case in handleNavigate. Its route string ('/apps') is kept only as a stable,
// human-readable identifier for that special-case check and for AppSidebar's
// key/aria-current wiring; it is deliberately absent from VIEW_TO_ROUTE/ROUTE_TO_VIEW since
// it no longer maps to any MenuView.
const DEV_TOOLS_ROUTE = '/apps';

const NAV_ELEMENTS: NavElement[] = [
  { name: 'Dashboard', route: '/dashboard',     icon: <DashboardNavIcon size={18} /> },
  { name: 'Designer',  route: '/designer',      icon: <DesignerNavIcon size={18} /> },
  { name: 'Settings',  route: '/settings',      icon: <SettingsNavIcon size={18} /> },
  { name: 'Dev Tools', route: DEV_TOOLS_ROUTE,  icon: <PackageIcon size={18} /> },
  { name: 'Observer',  route: '/observer',      icon: <EyeIcon size={18} /> },
  { name: 'HIL Test',  route: '/hil-test',      icon: <InboxIcon size={18} /> },
];

const VIEW_TO_ROUTE: Record<MenuView, string> = {
  dashboard:  '/dashboard',
  designer:   '/designer',
  settings:   '/settings',
  observer:   '/observer',
  'hil-test': '/hil-test',
};

const ROUTE_TO_VIEW: Record<string, MenuView> = {
  '/dashboard': 'dashboard',
  '/designer':  'designer',
  '/settings':  'settings',
  '/observer':  'observer',
  '/hil-test':  'hil-test',
};

const NAV_COLLAPSED_KEY = 'flow-studio-nav-collapsed';

// Header identity/notification wiring — same convention as app-template/
// saas-tenant-administration's AppShell.tsx. No Developer Tools row here deliberately:
// UserMenuButton only renders that row when `devTools` is a non-empty array (see its
// `showDevTools` check), so simply omitting the prop below hides it — this sidebar already
// has a first-class "Dev Tools" nav item (see NAV_ELEMENTS below) pointing at different
// content, and showing both would be confusing duplicate navigation.
//
// "View App Settings" is NOT similarly omittable — UserMenuButton renders that row
// unconditionally regardless of whether `appSettingsUrl` is supplied (verified directly
// against UserMenuButton.tsx: only the two "Tools" rows are gated on prop presence). Rather
// than leave it a dead, inert row with no href, APP_SETTINGS_URL below points it at this
// app's own "Settings" nav item — the two are now the same destination reached two ways
// (header dropdown + sidebar), not a duplicate pointing at different content. WorkflowApp.tsx
// reads window.location.pathname on mount (ROUTE_TO_INITIAL_VIEW) so this full-page-reload
// link actually lands on Settings instead of resetting to the dashboard.
//
// "Change Password" isn't a direct header row either — it's reached via ProfilePage's own
// "Change Password" link, one click past PROFILE_URL, matching every other migrated app.
const PROFILE_URL = '/profile';
const APP_SETTINGS_URL = '/settings';
const SIGN_IN_URL = '/login';
const NOTIFICATIONS_VIEW_ALL_URL = '/notifications';

// Same placeholder caveat as app-template/saas-tenant-administration — no Communication API
// is reachable from this dev environment either. A real deployment points this at its
// actual Notifications API host.
const NOTIFICATIONS_API_BASE_URL = import.meta.env.VITE_NOTIFICATIONS_API_BASE_URL || window.location.origin;

// ─── Component ───────────────────────────────────────────────────────────────

function handleBadgeInteract(_id: string, strategy: BadgeInteractionStrategy) {
  if (strategy.type === 'activateFlowObserverTab') {
    FlowObserverPanelEngine.activateTab(strategy.tabId);
  }
  // 'custom' actions would be routed here in future
}

function translate(key: string): string {
  // Basic fallback — replace with i18n provider when available
  const map: Record<string, string> = {
    'flowFooter.ariaLabel':    'Application status',
    'flowFooter.group.left':   'Workflow status',
    'flowFooter.group.right':  'System status',
    'flowFooter.connection':   'Connected',
    'flowFooter.designer.ready': 'Designer: Ready',
    'flowFooter.execution.idle':      'Idle',
    'flowFooter.execution.running':   'Running',
    'flowFooter.execution.error':     'Error',
    // 'cancelled' was already referenced by useFlowObserverSignalR.ts before this fix (falls
    // through to the raw key today, same gap 'paused'/'waiting' just had) -- added all three
    // together rather than leaving 'cancelled' as a known pre-existing miss.
    'flowFooter.execution.cancelled': 'Cancelled',
    'flowFooter.execution.paused':    'Paused',
    'flowFooter.execution.waiting':   'Waiting (HIL)',
    'flowFooter.notifications':     'Notifications',
    'flowFooter.appVersion':        'v1.0.0',
    'flowFooter.activeWorkflow':    'Workflow open',
  };
  return map[key] ?? key;
}

export const AppLayout: React.FC<AppLayoutProps> = ({
  children,
  activeView = 'dashboard',
  onViewChange,
  headerProps,
  onBuildWithAI,
}) => {

  const { theme } = useUIStore();
  const { token, isAuthenticated } = useAuthStore();
  const { appTools } = useGlobalAppData();

  const { items: notificationItems, unreadCount } = useNotifications({
    apiBaseUrl: NOTIFICATIONS_API_BASE_URL,
    getAuthToken: () => token,
    // userID intentionally omitted — @passport/store's user.id is a string of unconfirmed
    // shape. Omitting it uses the hook's documented fallback (a single search/unread call),
    // which needs no user identifier and still gives an accurate unread count + recent list.
    enabled: isAuthenticated,
  });

  const footerScope: BadgeVisibilityScope =
    activeView === 'designer' ? 'designer' :
    activeView === 'settings' ? 'settings' :
    'dashboard';

  // Mobile/tablet should default to collapsed (drawer closed)
  // Desktop respects user's saved preference from localStorage
  const [navCollapsed, setNavCollapsed] = useState<boolean>(
    () => {
      const isMobile = typeof window !== 'undefined' && window.innerWidth < DESKTOP_MIN_WIDTH;
      if (isMobile) return true; // Always collapsed on mobile/tablet
      // On desktop, respect user's saved preference
      return (localStorage.getItem(NAV_COLLAPSED_KEY) ?? 'true') === 'true';
    },
  );

  // Sync theme to <html> AND <body> so [data-theme="dark"/"light"] CSS selectors apply.
  // <html> was missing (real prod bug): CSS custom properties only inherit DOWN the DOM tree,
  // so bizfirst-common-themes' `[data-theme="light"] { --bg-primary: #fff }` override — matched
  // only against <body>, since that's the only element this used to be set on — never reached
  // reset.css's `html, body { background-color: var(--bg-primary) }` rule as evaluated on <html>
  // itself (an ANCESTOR of body, not a descendant). <html> kept resolving --bg-primary from its
  // own unscoped dark-theme default, leaving a visible dark strip wherever <body>/<main> don't
  // cover the full viewport width (e.g. the scrollbar gutter) in light mode.
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    document.body.setAttribute('data-theme', theme);
  }, [theme]);

  // Persist sidebar collapsed state. Only desktop widths write it: mobile/tablet always start
  // collapsed, so saving from there would overwrite the user's desktop preference.
  useEffect(() => {
    // Re-evaluated each time the state changes, so it reads the width at the moment of the toggle.
    if (!shouldPersistNavCollapsed(window.innerWidth)) return;
    localStorage.setItem(NAV_COLLAPSED_KEY, String(navCollapsed));
  }, [navCollapsed]);

  // ── Navigation handler ────────────────────────────────────────────────────

  const handleNavigate = (route: string) => {
    // Dev Tools is a real cross-app navigation now (the "Apps" catalog it used to render
    // in-place moved to the standalone developer-portal app — see that app's
    // DevToolsPage.tsx), not an internal SPA view, so it is handled here first and never
    // reaches ROUTE_TO_VIEW/onViewChange at all. A full page navigation (not client-side
    // routing) is deliberate: developer-portal is a separate Vite app on its own origin.
    if (route === DEV_TOOLS_ROUTE) {
      // Check if Dev Tools portal is accessible before navigating
      if (!DEVELOPER_PORTAL_URL) {
        console.error('[AppLayout] Dev Tools portal URL is not configured');
        return;
      }

      // Attempt to navigate to the Dev Tools portal
      try {
        // DEVELOPER_PORTAL_URL may be configured as an origin-relative path (e.g.
        // "/developerportal", as this deployment's VITE_DEVELOPER_PORTAL_URL is set) rather
        // than a full absolute URL — resolve against the current origin so both forms work,
        // instead of letting `new URL()` throw on the relative form (real prod bug: threw
        // "Failed to construct 'URL': Invalid URL" and left Dev Tools completely unusable).
        const url = new URL(DEVELOPER_PORTAL_URL, window.location.origin);
        window.location.href = url.href;
      } catch (error) {
        console.error('[AppLayout] Invalid Dev Tools portal URL:', DEVELOPER_PORTAL_URL, error);
        // Fall back to showing an error — user can see it in console
        if (window.alert) {
          window.alert('Dev Tools is currently unavailable. Please try again later.');
        }
      }
      return;
    }
    const view = ROUTE_TO_VIEW[route];
    if (view) {
      onViewChange?.(view);
    }
  };

  // ── Mobile/Tablet detection ──────────────────────────────────────────────
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [isTablet, setIsTablet] = useState<boolean>(false);

  // Whether the previous viewport check was phone-sized. null until the first check, so mount does not
  // count as a crossing (the store's initial leftPanelOpen already reflects the starting width).
  const wasMobileRef = useRef<boolean | null>(null);

  useEffect(() => {
    const checkViewport = () => {
      const width = window.innerWidth;
      const mobileNow = width < MOBILE_MAX_WIDTH;
      setIsMobile(mobileNow);
      setIsTablet(width >= MOBILE_MAX_WIDTH && width < DESKTOP_MIN_WIDTH);
      // Crossing from desktop/tablet width into phone width closes an open palette, so it does not cover
      // the canvas. Crossing back does NOT reopen it: the user's choice stays closed until they open it.
      if (mobileNow && wasMobileRef.current === false && useUIStore.getState().leftPanelOpen) {
        useUIStore.getState().setLeftPanelOpen(false);
      }
      wasMobileRef.current = mobileNow;
    };

    checkViewport();
    window.addEventListener('resize', checkViewport);

    return () => window.removeEventListener('resize', checkViewport);
  }, []);

  // ── Backdrop click handler ───────────────────────────────────────────────

  const handleBackdropClick = () => {
    if ((isMobile || isTablet) && !navCollapsed) {
      setNavCollapsed(true);
    }
  };

  // Escape closes the mobile/tablet nav drawer, matching every other overlay in this app
  // (Create Project modal, Delete-confirm dialog) — previously only a tap on the backdrop or
  // the hamburger icon itself could dismiss it, which was easy to get stuck on since nothing
  // else about the drawer signals it's a modal overlay (see the backdrop it already renders,
  // just above, for mobile/tablet).
  useEffect(() => {
    if (!(isMobile || isTablet) || navCollapsed) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      // A dialog that is open or that handled this Escape owns the key (see Support/Modal). Closing the
      // drawer as well would dismiss the drawer behind a dialog the user is still working in.
      if (e.key === 'Escape' && !e.defaultPrevented && !isAnyModalOpen()) setNavCollapsed(true);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isMobile, isTablet, navCollapsed]);

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className={`app flow-studio-shell${navCollapsed ? ' nav-collapsed' : ''}`}>
      {/* Backdrop: close sidebar on click (mobile & tablet) */}
      {(isMobile || isTablet) && !navCollapsed && (
        <div
          className="mobile-backdrop"
          onClick={handleBackdropClick}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.35)',      /* Semi-transparent */
            backdropFilter: 'blur(2px)',            /* Subtle blur */
            WebkitBackdropFilter: 'blur(2px)',      /* Safari */
            zIndex: 999,
            animation: 'fadeIn 0.3s ease',
          }}
        />
      )}

      <AppHeader
        title="Flow Studio"
        navCollapsed={navCollapsed}
        onToggleSidebar={() => setNavCollapsed((prev) => !prev)}
        rightContent={
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {onBuildWithAI && <BuildWithAIButton onClick={onBuildWithAI} theme="dark" />}
            <AuthHeaderControls
              profileUrl={PROFILE_URL}
              appSettingsUrl={APP_SETTINGS_URL}
              signInUrl={SIGN_IN_URL}
              // No devTools here — see the comment above APP_SETTINGS_URL for why: this
              // sidebar already has a first-class "Dev Tools" nav item pointing at different
              // content, and this row is genuinely omittable (unlike "View App Settings").
              appTools={appTools}
              // UserMenuButton's own handleLogout already sends the browser to the login
              // app's dedicated /logout route unconditionally (not /login — that would
              // silently reuse whatever session the login app's own origin still has,
              // undoing the logout the user just asked for) — no onAfterLogout override
              // needed here. handoffMode="code" matches the consuming app's own
              // <LoginRedirectPage handoffMode="code"> (see App.tsx): without it, /logout
              // wouldn't carry the handoff preference through, and the next sign-in would
              // silently fall back to the legacy ?token=<raw JWT> handoff (confirmed via
              // live HAR capture).
              handoffMode="code"
              notifications={{
                items: notificationItems.map((item) => ({
                  id: String(item.notificationID),
                  title: item.title,
                  body: item.message,
                  timestamp: item.sentAt ?? item.scheduledAt ?? '',
                  read: !isNotificationUnread(item),
                  href: item.actionUrl ?? undefined,
                })),
                unreadCount,
                // No onMarkAllRead — the real backend has no HTTP route for MarkReadAsync yet
                // (see @bizfirst/notifications-react's useNotifications.ts).
                viewAllUrl: NOTIFICATIONS_VIEW_ALL_URL,
              }}
            />
            <BizFirstLinks />
          </div>
        }
      />

      {/*
       * AppSidebar — flat `elements` mode (no submenus).
       * When items have NO submenus, clicking navigates directly.
       * Sidebar expand/collapse is controlled by the AppHeader toggle button.
       *
       * On mobile/tablet: Only render when menu is open (not collapsed)
       * On desktop: Always render
       *
       * To enable dropdown submenus for future menu groups, replace `elements`
       * with a `groups` prop containing NavGroup[] entries.
       */}
      {((!isMobile && !isTablet) || !navCollapsed) && (
        <AppSidebar
          elements={NAV_ELEMENTS}
          currentPath={VIEW_TO_ROUTE[activeView] ?? '/dashboard'}
          navCollapsed={navCollapsed}
          navigate={handleNavigate}
          theme={theme}
        />
      )}

      <main className="app-main">
        {headerProps?.breadcrumbs && headerProps.breadcrumbs.length > 0 && (
          <nav className="app-breadcrumbs">
            {headerProps.breadcrumbs.map((crumb, i) => (
              <span key={i}>
                {i > 0 && <span className="breadcrumb-sep"> / </span>}
                {crumb.onClick ? (
                  <button className="breadcrumb-link" onClick={crumb.onClick}>{crumb.label}</button>
                ) : (
                  <span className="breadcrumb-current">{crumb.label}</span>
                )}
              </span>
            ))}
          </nav>
        )}
        {children}
      </main>

      <FlowFooter
        currentScope={footerScope}
        theme={theme}
        onBadgeInteract={handleBadgeInteract}
        translate={translate}
      />
    </div>
  );
};
